import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import { apolloClient } from 'core/apollo-client';
import { SEND_POLICY_INVOICE } from 'customer/components/policy/payment-tab/payment.queries';
import useStyles from './invoice-history.styles';

/**
 * Resend from the invoice history (#36679): sends a FRESH invoice for the policy's current amount
 * due, the same request as the billing tab's Send Invoice (#34203, #36678), not the past copy.
 */
export function ResendInvoiceDialog({ policyId, hasEmail, onClose }) {
  const classes = useStyles();
  const toast = useToast();
  const [sendEmail, setSendEmail] = useState(hasEmail);
  const [sendLetter, setSendLetter] = useState(!hasEmail);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await apolloClient.mutate({
        mutation: SEND_POLICY_INVOICE,
        fetchPolicy: 'no-cache',
        variables: { args: { policyId, sendEmail, sendLetter, generateOnly: false }, taskRunId: uuid() }
      });
      const result = data?.toolsSendPolicyInvoice;
      if (result?.success) {
        let details = {};
        try {
          details = result.details ? JSON.parse(result.details) : {};
        } catch {
          details = {};
        }
        // The letter is dropped server-side when one was mailed to this policy in the last 7 days
        // (#34203); the dialog can't see the balance, so the success copy says what happens if none is due.
        toast.notify(
          details.letterSkippedRecentlyMailed
            ? {
                type: 'info',
                message:
                  'A letter was already mailed to this policy in the last 7 days, so the letter was skipped. The email was sent.',
                durationInSeconds: 10
              }
            : {
                type: 'success',
                message:
                  'Resend requested. It can take a few minutes to be delivered. If nothing is due, no invoice is sent.'
              }
        );
        onClose();
      } else {
        toast.notify({ type: 'error', message: result?.error || 'Could not send the invoice.' });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: `An error occurred. ${error.message.replace(':', '').trim()}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <BaseDialog open onClose={onClose} className={classes.dialog}>
      <div className={classes.dialogContent}>
        <Label type="h1">Resend invoice</Label>
        <Label type="body3">
          Sends a new invoice for policy {policyId} with the amount due now, or the next installment if nothing is due
          yet. If the policy is paid in full, nothing is sent.
        </Label>
        <Checkbox
          id="resend-invoice-email"
          mode="light"
          label={hasEmail ? 'Email' : 'Email — no email address on file'}
          value={sendEmail}
          disabled={!hasEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <Checkbox
          id="resend-invoice-letter"
          mode="light"
          label="Letter (mailed)"
          value={sendLetter}
          onChange={(e) => setSendLetter(e.target.checked)}
        />
        <div className={classes.dialogActions}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSend}
            disabled={(!sendEmail && !sendLetter) || sending}
            loading={sending}
          >
            Send
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
}

ResendInvoiceDialog.propTypes = {
  policyId: PropTypes.string.isRequired,
  hasEmail: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
